import { ModuleTypeEnum } from './module'
import { AccountType } from './organization'
import { PermissionActionEnum } from './permissions'

/**
 * InfoPanel Widget Types
 * Widgets that can be embedded in InfoPanel sections alongside fields and tables.
 */

export enum InfoPanelWidgetType {
  HOLIDAYS = 'holidays',
  LINKS = 'links',
  QUICK_LINKS = 'quick-links',
  EMBED = 'embed',
  IMAGE = 'image',
  RICH_TEXT = 'rich-text',
  STAT_CARDS = 'stat-cards',
  ANNOUNCEMENT = 'announcement',
  // Insight widgets — data-driven summary cards scoped to the current record
  STAFFING_SUMMARY = 'staffing-summary',
  RECENT_TIME = 'recent-time',
  PROJECT_ALERTS = 'project-alerts',
  BUDGET_HEALTH = 'budget-health',
  MARGIN_SNAPSHOT = 'margin-snapshot',
  // Member profile insight widgets
  MEMBER_ALLOCATIONS = 'member-allocations',
  MEMBER_UTILIZATION = 'member-utilization',
  MEMBER_RECENT_TIME = 'member-recent-time',
  MEMBER_TIME_OFF = 'member-time-off',
  // Personal (family) space insight widgets — Home Base overview
  FAMILY_SNAPSHOT = 'family-snapshot',
  LISTS_OVERVIEW = 'lists-overview',
  WEEK_AHEAD = 'week-ahead',
}

/**
 * 'content' widgets hold user-authored content (links, text, images).
 * 'insight' widgets render live data for the record they are embedded on and
 * can be toggled per user on the overview page.
 */
export type InfoPanelWidgetCategory = 'content' | 'insight'

/**
 * Declarative access requirements for a widget. A widget is visible when the
 * user passes ALL systemPermissions (org owners/admins always pass), OR is
 * granted ANY of recordCapabilities by their record-level role. An explicit
 * record-level deny of a listed capability always hides the widget.
 */
export interface InfoPanelWidgetAccess {
  systemPermissions?: Array<{
    module: ModuleTypeEnum
    action: PermissionActionEnum
  }>
  recordCapabilities?: string[]
  /**
   * Restrict this widget to specific account types. Absent = available to all
   * account types. Business insight widgets (staffing, budget, margin,
   * utilization) are gated to ORGANIZATION so they never leak onto a personal
   * account's records, where the org owner passes every permission check.
   */
  accountTypes?: AccountType[]
}

/**
 * Widget definition for registry
 */
export interface InfoPanelWidgetDefinition {
  type: InfoPanelWidgetType
  label: string
  description: string
  icon: string
  // Which modules can use this widget (empty array = available everywhere)
  allowedModules: ModuleTypeEnum[]
  // Default settings for new instances
  defaultSettings: Record<string, unknown>
  // Whether widget needs persisted data storage (e.g., links widget stores link items)
  hasPersistedData: boolean
  // Widget category (defaults to 'content')
  category?: InfoPanelWidgetCategory
  // Permission requirements — widget is hidden everywhere (render, add dialog,
  // toggles) for users who don't satisfy them
  access?: InfoPanelWidgetAccess
}

/**
 * Widget registry - all available widgets
 */
export const INFO_PANEL_WIDGETS: InfoPanelWidgetDefinition[] = [
  {
    type: InfoPanelWidgetType.HOLIDAYS,
    label: 'Holidays',
    description: 'Display upcoming company holidays',
    icon: 'Calendar',
    allowedModules: [],
    defaultSettings: {
      maxItems: 5,
      dateRange: 'quarter',
      showPast: false,
    },
    hasPersistedData: false, // Uses existing OrganizationHoliday data
  },
  {
    type: InfoPanelWidgetType.LINKS,
    label: 'Links',
    description: 'Custom list of links and resources',
    icon: 'Link',
    allowedModules: [],
    defaultSettings: {
      displayStyle: 'list',
      showDescriptions: true,
      showIcons: true,
    },
    hasPersistedData: true, // Stores link items
  },
  {
    type: InfoPanelWidgetType.QUICK_LINKS,
    label: 'Quick links',
    description: 'Grid of icon shortcuts for fast access',
    icon: 'SquaresFour',
    allowedModules: [],
    defaultSettings: {
      columns: 4,
      iconSize: 'medium',
    },
    hasPersistedData: true, // Stores quick link items
  },
  {
    type: InfoPanelWidgetType.EMBED,
    label: 'Embed',
    description: 'Embed external content (video, docs, iframe)',
    icon: 'Code',
    allowedModules: [],
    defaultSettings: {
      embedType: 'iframe',
      url: '',
      height: 400,
      allowFullscreen: true,
    },
    hasPersistedData: false, // URL stored in widget settings
  },
  {
    type: InfoPanelWidgetType.IMAGE,
    label: 'Image',
    description: 'Display an image with optional caption and link',
    icon: 'Image',
    allowedModules: [],
    defaultSettings: {
      aspectRatio: 'auto',
      fit: 'cover',
      borderRadius: 12,
      showCaption: false,
      caption: '',
      linkUrl: '',
    },
    hasPersistedData: false, // URL stored in widget settings
  },
  {
    type: InfoPanelWidgetType.RICH_TEXT,
    label: 'Rich text',
    description: 'Editable content block with formatting',
    icon: 'TextT',
    allowedModules: [],
    defaultSettings: {
      content: '',
      alignment: 'left',
      showBorder: false,
    },
    hasPersistedData: true, // Stores rich text content
  },
  {
    type: InfoPanelWidgetType.STAT_CARDS,
    label: 'Stats',
    description: 'Display key metrics with trend indicators',
    icon: 'ChartBar',
    allowedModules: [],
    defaultSettings: {
      layout: 'row', // 'row' | 'grid'
      columns: 3,
    },
    hasPersistedData: true, // Stores stat card items
  },
  {
    type: InfoPanelWidgetType.ANNOUNCEMENT,
    label: 'Announcement',
    description: 'Pinned announcement banner with priority styling',
    icon: 'Megaphone',
    allowedModules: [],
    defaultSettings: {
      priority: 'info', // 'info' | 'warning' | 'success' | 'urgent'
      title: '',
      message: '',
      showIcon: true,
      dismissible: false,
      linkUrl: '',
      linkText: '',
    },
    hasPersistedData: true, // Stores announcement content and display settings
  },
  // -----------------------------------------------------------------------
  // Insight widgets (project overview summary cards)
  // -----------------------------------------------------------------------
  {
    type: InfoPanelWidgetType.STAFFING_SUMMARY,
    label: 'Staffing summary',
    description:
      'Team size, confirmed hours per week, and open staffing requests',
    icon: 'UsersThree',
    allowedModules: [ModuleTypeEnum.PROJECT],
    category: 'insight',
    defaultSettings: {},
    hasPersistedData: false, // Live data from the project overview summary
    // Same gate as the project Staffing tab: headcount and planned hours are
    // staffing data, not something every project member should read off the
    // overview. The server omits staffingSummary for anyone else.
    access: {
      systemPermissions: [
        { module: ModuleTypeEnum.PROJECT, action: PermissionActionEnum.MANAGE },
        {
          module: ModuleTypeEnum.FINANCIALS,
          action: PermissionActionEnum.READ,
        },
      ],
      accountTypes: [AccountType.ORGANIZATION],
    },
  },
  {
    type: InfoPanelWidgetType.RECENT_TIME,
    label: 'Recent time',
    description: 'Latest time entries logged on this project',
    icon: 'ClockCounterClockwise',
    allowedModules: [ModuleTypeEnum.PROJECT],
    category: 'insight',
    defaultSettings: {
      maxItems: 3,
    },
    hasPersistedData: false,
    // Visible to anyone with access to the project record (project members,
    // org PROJECT managers, org owners/admins) — mirrors the server, which
    // asserts record read access on the overview-summary endpoint.
    access: {
      recordCapabilities: ['project.read'],
      accountTypes: [AccountType.ORGANIZATION],
    },
  },
  {
    type: InfoPanelWidgetType.PROJECT_ALERTS,
    label: 'Alerts',
    description: 'Staffing, time, and demand alerts for this project',
    icon: 'WarningCircle',
    allowedModules: [ModuleTypeEnum.PROJECT],
    category: 'insight',
    defaultSettings: {},
    hasPersistedData: false,
    // Conflicts, staffing requests and demand are staffing data: same gate
    // as Staffing Summary. The server omits alerts for anyone else.
    access: {
      systemPermissions: [
        { module: ModuleTypeEnum.PROJECT, action: PermissionActionEnum.MANAGE },
        {
          module: ModuleTypeEnum.FINANCIALS,
          action: PermissionActionEnum.READ,
        },
      ],
      accountTypes: [AccountType.ORGANIZATION],
    },
  },
  {
    type: InfoPanelWidgetType.BUDGET_HEALTH,
    label: 'Budget health',
    description: 'Budget status, actual spend, and burn rate',
    icon: 'Wallet',
    allowedModules: [ModuleTypeEnum.PROJECT],
    category: 'insight',
    defaultSettings: {},
    hasPersistedData: false,
    // Mirrors the server's cost-data visibility rule (TEAM READ) with a
    // record-level escape hatch for financial-viewer style roles.
    access: {
      systemPermissions: [
        { module: ModuleTypeEnum.TEAM, action: PermissionActionEnum.READ },
      ],
      recordCapabilities: ['project.field.budget.read'],
      accountTypes: [AccountType.ORGANIZATION],
    },
  },
  {
    type: InfoPanelWidgetType.MARGIN_SNAPSHOT,
    label: 'Margin snapshot',
    description: 'Current margin percentage and trend vs last period',
    icon: 'ChartLineUp',
    allowedModules: [ModuleTypeEnum.PROJECT],
    category: 'insight',
    defaultSettings: {},
    hasPersistedData: false,
    // Margin needs both revenue (PROJECT READ) and cost (TEAM READ) data,
    // matching the server's FinancialVisibilityService flags.
    access: {
      systemPermissions: [
        { module: ModuleTypeEnum.PROJECT, action: PermissionActionEnum.READ },
        { module: ModuleTypeEnum.TEAM, action: PermissionActionEnum.READ },
      ],
      recordCapabilities: ['project.field.margin.read'],
      accountTypes: [AccountType.ORGANIZATION],
    },
  },
  // -----------------------------------------------------------------------
  // Member profile insight widgets — all require TEAM READ, matching the
  // previous hardcoded cards' visibility rule
  // -----------------------------------------------------------------------
  {
    type: InfoPanelWidgetType.MEMBER_ALLOCATIONS,
    label: 'Allocations',
    description: 'Current and upcoming confirmed or tentative allocations',
    icon: 'Briefcase',
    allowedModules: [ModuleTypeEnum.MEMBER],
    category: 'insight',
    defaultSettings: {},
    hasPersistedData: false,
    access: {
      systemPermissions: [
        { module: ModuleTypeEnum.TEAM, action: PermissionActionEnum.READ },
      ],
      accountTypes: [AccountType.ORGANIZATION],
    },
  },
  {
    type: InfoPanelWidgetType.MEMBER_UTILIZATION,
    label: 'Utilization snapshot',
    description: 'Last 4 weeks of utilization vs the member target',
    icon: 'ChartBar',
    allowedModules: [ModuleTypeEnum.MEMBER],
    category: 'insight',
    defaultSettings: {},
    hasPersistedData: false,
    access: {
      systemPermissions: [
        { module: ModuleTypeEnum.TEAM, action: PermissionActionEnum.READ },
      ],
      accountTypes: [AccountType.ORGANIZATION],
    },
  },
  {
    type: InfoPanelWidgetType.MEMBER_RECENT_TIME,
    label: 'Recent time entries',
    description: 'Latest time entries logged by this member',
    icon: 'ClockCounterClockwise',
    allowedModules: [ModuleTypeEnum.MEMBER],
    category: 'insight',
    defaultSettings: {
      maxItems: 5,
    },
    hasPersistedData: false,
    access: {
      systemPermissions: [
        { module: ModuleTypeEnum.TEAM, action: PermissionActionEnum.READ },
      ],
      accountTypes: [AccountType.ORGANIZATION],
    },
  },
  {
    type: InfoPanelWidgetType.MEMBER_TIME_OFF,
    label: 'Upcoming time off',
    description: 'Approved and pending schedule exceptions',
    icon: 'Calendar',
    allowedModules: [ModuleTypeEnum.MEMBER],
    category: 'insight',
    defaultSettings: {},
    hasPersistedData: false,
    access: {
      systemPermissions: [
        { module: ModuleTypeEnum.TEAM, action: PermissionActionEnum.READ },
      ],
      accountTypes: [AccountType.ORGANIZATION],
    },
  },
  // -----------------------------------------------------------------------
  // Personal (family) space insight widgets — Home Base overview. Gated to
  // PERSONAL so they never surface on a business org's My Organization page
  // (the account-type gate wins over every permission grant).
  // -----------------------------------------------------------------------
  {
    type: InfoPanelWidgetType.FAMILY_SNAPSHOT,
    label: 'Family snapshot',
    description: 'Stars earned this week and current streaks for your family',
    icon: 'Star',
    allowedModules: [ModuleTypeEnum.ORGANIZATION],
    category: 'insight',
    defaultSettings: {},
    hasPersistedData: false,
    access: {
      accountTypes: [AccountType.PERSONAL],
    },
  },
  {
    type: InfoPanelWidgetType.LISTS_OVERVIEW,
    label: 'Lists',
    description: 'Your grocery, packing, and household lists with open counts',
    icon: 'ListChecks',
    allowedModules: [ModuleTypeEnum.ORGANIZATION],
    category: 'insight',
    defaultSettings: {},
    hasPersistedData: false,
    access: {
      accountTypes: [AccountType.PERSONAL],
    },
  },
  {
    type: InfoPanelWidgetType.WEEK_AHEAD,
    label: 'This week',
    description: 'Everything due in the next seven days across your space',
    icon: 'CalendarCheck',
    allowedModules: [ModuleTypeEnum.ORGANIZATION],
    category: 'insight',
    defaultSettings: {},
    hasPersistedData: false,
    access: {
      accountTypes: [AccountType.PERSONAL],
    },
  },
]

/**
 * Get widget definition by type
 */
export function getWidgetDefinition(
  type: InfoPanelWidgetType,
): InfoPanelWidgetDefinition | undefined {
  return INFO_PANEL_WIDGETS.find(w => w.type === type)
}

/**
 * All insight widget types (data-driven summary cards)
 */
export const INSIGHT_WIDGET_TYPES: InfoPanelWidgetType[] =
  INFO_PANEL_WIDGETS.filter(w => w.category === 'insight').map(w => w.type)

/**
 * Whether a widget type (from a persisted layout, so possibly unknown) is an insight widget
 */
export function isInsightWidgetType(type: string): boolean {
  return (INSIGHT_WIDGET_TYPES as string[]).includes(type)
}

/**
 * Get insight widget definitions available for a module type
 */
export function getInsightWidgetsForModule(
  moduleType: ModuleTypeEnum,
): InfoPanelWidgetDefinition[] {
  return INFO_PANEL_WIDGETS.filter(
    w => w.category === 'insight' && w.allowedModules.includes(moduleType),
  )
}

/**
 * Get available widgets for a module type
 */
export function getAvailableWidgets(
  moduleType?: ModuleTypeEnum,
): InfoPanelWidgetDefinition[] {
  return INFO_PANEL_WIDGETS.filter(widget => {
    // If no allowed modules specified, widget is available everywhere
    if (widget.allowedModules.length === 0) return true
    // If moduleType provided, check if it's in the allowed list
    if (moduleType) {
      return widget.allowedModules.includes(moduleType)
    }
    return true
  })
}

// ========================================
// Widget Settings Types
// ========================================

export interface HolidaysWidgetSettings {
  maxItems?: number
  dateRange?: 'month' | 'quarter' | 'year'
  showPast?: boolean
}

export interface LinksWidgetSettings {
  displayStyle?: 'list' | 'grid'
  showDescriptions?: boolean
  showIcons?: boolean
}

export interface QuickLinksWidgetSettings {
  columns?: 2 | 3 | 4 | 6
  iconSize?: 'small' | 'medium' | 'large'
}

export interface EmbedWidgetSettings {
  embedType?: 'iframe' | 'video' | 'document'
  url?: string
  height?: number
  allowFullscreen?: boolean
}

export interface ImageWidgetSettings {
  imageUrl?: string
  aspectRatio?: 'auto' | '16:9' | '4:3' | '1:1' | '3:2'
  fit?: 'cover' | 'contain' | 'fill'
  borderRadius?: number
  showCaption?: boolean
  caption?: string
  linkUrl?: string
  alt?: string
}

export interface RichTextWidgetSettings {
  content?: string
  alignment?: 'left' | 'center' | 'right'
  showBorder?: boolean
}

export interface StatCardItem {
  id: string
  label: string
  value: string | number
  prefix?: string
  suffix?: string
  trend?: 'up' | 'down' | 'neutral'
  trendValue?: string
  icon?: string
  color?: string
  order: number
}

export interface StatCardsWidgetSettings {
  layout?: 'row' | 'grid'
  columns?: 2 | 3 | 4
}

export interface RecentTimeWidgetSettings {
  maxItems?: number
}

export type AnnouncementPriority = 'info' | 'warning' | 'success' | 'urgent'

export interface AnnouncementWidgetSettings {
  priority?: AnnouncementPriority
  title?: string
  message?: string
  showIcon?: boolean
  dismissible?: boolean
  linkUrl?: string
  linkText?: string
}

export interface AnnouncementItem extends AnnouncementWidgetSettings {
  id: string
  order: number
}

export interface AnnouncementWidgetData extends AnnouncementWidgetSettings {
  announcements?: AnnouncementItem[]
  updatedAt?: string
}

export type InfoPanelWidgetSettings =
  | HolidaysWidgetSettings
  | LinksWidgetSettings
  | QuickLinksWidgetSettings
  | EmbedWidgetSettings
  | ImageWidgetSettings
  | RichTextWidgetSettings
  | StatCardsWidgetSettings
  | AnnouncementWidgetSettings

// ========================================
// Widget Data Types (for persisted widgets)
// ========================================

export interface LinkItem {
  id: string
  label: string
  url: string
  icon?: string
  description?: string
  order: number
}

export interface QuickLinkItem {
  id: string
  label: string
  url: string
  icon: string
  color?: string
  order: number
}

export interface RichTextWidgetData {
  content: string
  lastUpdatedBy?: string
}

export interface InfoPanelWidgetData {
  id: string
  organizationId: string
  widgetType: InfoPanelWidgetType
  instanceId: string
  moduleType?: string
  moduleId?: string
  data:
    | LinkItem[]
    | QuickLinkItem[]
    | StatCardItem[]
    | RichTextWidgetData
    | AnnouncementWidgetData
    | Record<string, unknown>
  createdAt: string
  updatedAt: string
  createdBy: string
}

export interface CreateWidgetDataDTO {
  widgetType: InfoPanelWidgetType
  instanceId: string
  moduleType?: string
  moduleId?: string
  data:
    | LinkItem[]
    | QuickLinkItem[]
    | StatCardItem[]
    | RichTextWidgetData
    | AnnouncementWidgetData
    | Record<string, unknown>
}

export interface UpdateWidgetDataDTO {
  data:
    | LinkItem[]
    | QuickLinkItem[]
    | StatCardItem[]
    | RichTextWidgetData
    | AnnouncementWidgetData
    | Record<string, unknown>
}

/**
 * Base props that ALL InfoPanel widgets must accept.
 * Individual widgets can extend this with widget-specific props.
 */
export interface BaseWidgetProps {
  instanceId: string
  title?: string
  settings: Record<string, unknown>
  moduleType: ModuleTypeEnum
  // Id of the record the panel is rendered for (e.g. project id). Required by
  // insight widgets to fetch record-scoped data.
  moduleId?: string
  readOnly?: boolean
  hideActions?: boolean
}
